(() => {
  class EverPatchBundleBuilder {
    constructor(root) {
      this.root = root;
      this.sizeButtons = [...root.querySelectorAll('[data-bundle-size]')];
      this.variantNodes = [...root.querySelectorAll('[data-bundle-variant]')];
      this.cards = [...root.querySelectorAll('[data-patch-card]')];
      this.search = root.querySelector('[data-bundle-search]');
      this.clearSearch = root.querySelector('[data-clear-search]');
      this.resultCount = root.querySelector('[data-result-count]');
      this.selected = [];
      this.size = null;
      this.bundleVariant = null;
      this.bundleVariants = this.readBundleVariants();
      this.currency = root.dataset.currency || 'USD';
      this.locale = root.dataset.locale || 'en-US';
      this.storageKey = `everpatch-bundle:${location.pathname}`;
      this.addButtons = [...root.querySelectorAll('[data-add-bundle]')];
      this.progressBars = [...root.querySelectorAll('[data-progress-bar]')];
      this.countNodes = [...root.querySelectorAll('[data-selected-count]')];
      this.subNodes = [...root.querySelectorAll('[data-selected-sub]')];
      this.priceNodes = [...root.querySelectorAll('[data-bundle-price]')];
      this.statusDots = [...root.querySelectorAll('[data-status-dot]')];
      this.selectedList = root.querySelector('[data-selected-list]');
      this.mobileThumbs = root.querySelector('[data-mobile-thumbs]');
      this.messageNodes = [...root.querySelectorAll('[data-bundle-message]')];
      this.modal = root.querySelector('[data-quick-modal]');
      this.modalTitle = root.querySelector('[data-modal-title]');
      this.modalImage = root.querySelector('[data-modal-image]');
      this.modalSelect = root.querySelector('[data-modal-select]');
      this.modalHandle = null;
      this.sortCards();
      this.aliases = {
        'cali': 'california', 'nyc': 'new york', 'nola': 'new orleans',
        'pnw': 'washington oregon pacific northwest', 'grand canyon': 'arizona',
        'big sur': 'california coast pacific', 'yellowstone': 'wyoming montana idaho',
        'zion': 'utah', 'bryce': 'utah', 'arches': 'utah', 'canyonlands': 'utah',
        'olympic': 'washington', 'rainier': 'washington', 'yosemite': 'california',
        'sequoia': 'california', 'acadia': 'maine', 'everglades': 'florida'
      };
      this.bind();
      this.restore();
      this.filterCards();
      this.render();
    }

    sortCards() {
      const grid = this.root.querySelector('.ep-bundle__grid');
      if (!grid) return;
      this.cards.sort((a, b) => (a.dataset.name || '').localeCompare(b.dataset.name || '', undefined, { sensitivity: 'base' }));
      this.cards.forEach(card => grid.appendChild(card));
    }

    readBundleVariants() {
      const map = new Map();
      this.variantNodes.forEach(node => {
        const title = (node.dataset.title || '').toLowerCase();
        const match = title.match(/(?:^|\D)(10|5|3|2)(?:\D|$)/);
        if (!match) return;
        map.set(Number(match[1]), {
          id: Number(node.dataset.variantId),
          available: node.dataset.available === 'true',
          price: Number(node.dataset.price || 0),
          formattedPrice: node.dataset.formattedPrice || ''
        });
      });
      return map;
    }

    bind() {
      this.sizeButtons.forEach(btn => btn.addEventListener('click', () => this.setSize(Number(btn.dataset.bundleSize))));
      this.cards.forEach(card => {
        card.querySelector('[data-toggle-patch]')?.addEventListener('click', () => this.toggle(card.dataset.handle));
        card.querySelector('[data-preview-patch]')?.addEventListener('click', () => this.openModal(card.dataset.handle));
      });
      this.search?.addEventListener('input', () => this.filterCards());
      this.clearSearch?.addEventListener('click', () => { this.search.value = ''; this.search.focus(); this.filterCards(); });
      rootEvent(this.root, 'click', '[data-remove-patch]', (button) => this.remove(button.dataset.removePatch));
      this.addButtons.forEach(btn => btn.addEventListener('click', () => this.addBundleToCart(btn)));
      rootEvent(this.root, 'click', '[data-close-modal]', () => this.closeModal());
      this.modal?.addEventListener('click', e => { if (e.target.matches('[data-modal-backdrop]')) this.closeModal(); });
      this.modalSelect?.addEventListener('click', () => { if (this.modalHandle) this.toggle(this.modalHandle, true); this.closeModal(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal(); });
    }

    chooseInitialSize() {
      const first = [2,3,5,10].find(size => this.bundleVariants.get(size)?.available);
      if (first) this.setSize(first, false);
    }

    setSize(size, persist = true) {
      const variant = this.bundleVariants.get(size);
      if (!variant || !variant.available) return;
      this.size = size;
      this.bundleVariant = variant;
      if (this.selected.length > size) this.selected = this.selected.slice(0, size);
      this.sizeButtons.forEach(btn => {
        const active = Number(btn.dataset.bundleSize) === size;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (persist) this.persist();
      this.render();
    }

    cardFor(handle) { return this.cards.find(card => card.dataset.handle === handle); }

    toggle(handle, forceAdd = false) {
      const card = this.cardFor(handle);
      if (!card || card.dataset.available !== 'true') return;
      const index = this.selected.indexOf(handle);
      if (index >= 0 && !forceAdd) {
        this.selected.splice(index, 1);
      } else if (index < 0) {
        if (!this.size) return;
        if (this.selected.length >= this.size) {
          this.flash(`Your Pick ${this.size} is already full. Remove a patch to choose another.`);
          return;
        }
        this.selected.push(handle);
      }
      this.persist();
      this.render();
    }

    remove(handle) {
      this.selected = this.selected.filter(item => item !== handle);
      this.persist();
      this.render();
    }

    normalize(text) {
      return (text || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
    }

    expandedQuery(query) {
      let normalized = this.normalize(query);
      Object.entries(this.aliases).forEach(([key, value]) => {
        if (normalized.includes(key)) normalized += ` ${value}`;
      });
      return normalized;
    }

    filterCards() {
      const q = this.expandedQuery(this.search?.value || '');
      const terms = q.split(/\s+/).filter(Boolean);
      let count = 0;
      this.cards.forEach(card => {
        const haystack = this.normalize(`${card.dataset.name} ${card.dataset.search}`);
        const visible = !terms.length || terms.every(term => haystack.includes(term));
        card.classList.toggle('is-hidden', !visible);
        if (visible) count += 1;
      });
      if (this.resultCount) this.resultCount.textContent = `${count} patch${count === 1 ? '' : 'es'}`;
      this.root.querySelector('[data-empty-results]')?.classList.toggle('is-visible', count === 0);
      if (this.clearSearch) this.clearSearch.classList.toggle('is-visible', Boolean(this.search?.value));
    }

    render() {
      const count = this.selected.length;
      const target = this.size || 0;
      const remaining = Math.max(target - count, 0);
      const ready = target > 0 && count === target && this.bundleVariant?.available;
      const progress = target ? Math.min(100, (count / target) * 100) : 0;

      this.cards.forEach(card => {
        const selected = this.selected.includes(card.dataset.handle);
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-selected', selected ? 'true' : 'false');
        const button = card.querySelector('[data-toggle-patch]');
        if (button) button.textContent = selected ? '✓ Selected' : '+ Add';
      });

      this.progressBars.forEach(bar => bar.style.width = `${progress}%`);
      this.countNodes.forEach(node => node.textContent = `${count} / ${target || '—'}`);
      const sub = !target ? 'Pick a bundle size above' : ready ? 'Bundle complete — ready for cart' : `Tap Add on ${remaining} more ${remaining === 1 ? 'patch' : 'patches'}`;
      this.subNodes.forEach(node => node.textContent = sub);
      this.statusDots.forEach(dot => dot.classList.toggle('is-ready', ready));
      this.addButtons.forEach(button => {
        button.disabled = !ready;
        button.classList.toggle('is-ready', ready);
        button.textContent = ready ? `Add Pick ${target} Bundle to Cart` : (target ? `Add ${remaining} More ${remaining === 1 ? 'Patch' : 'Patches'}` : 'Pick a Bundle Size Above');
      });
      this.priceNodes.forEach(node => node.textContent = this.bundleVariant?.formattedPrice || '—');

      if (this.selectedList) {
        this.selectedList.innerHTML = '';
        if (!count) {
          const placeholder = document.createElement('div');
          placeholder.className = 'ep-console__placeholder';
          placeholder.textContent = 'Your selected destinations will appear here.';
          this.selectedList.appendChild(placeholder);
        } else {
          this.selected.forEach((handle, index) => {
            const card = this.cardFor(handle);
            if (!card) return;
            const row = document.createElement('div');
            row.className = 'ep-console__item';
            row.innerHTML = `
              <img src="${escapeHtml(card.dataset.image)}" alt="" loading="lazy">
              <div class="ep-console__item-name"><span>${index + 1}.</span> ${escapeHtml(card.dataset.name)}</div>
              <button type="button" class="ep-console__remove" data-remove-patch="${escapeHtml(handle)}" aria-label="Remove ${escapeHtml(card.dataset.name)}">×</button>`;
            this.selectedList.appendChild(row);
          });
        }
      }

      if (this.mobileThumbs) {
        this.mobileThumbs.innerHTML = this.selected.slice(0, 5).map(handle => {
          const card = this.cardFor(handle);
          return card ? `<img src="${escapeHtml(card.dataset.image)}" alt="">` : '';
        }).join('');
      }
    }

    openModal(handle) {
      const card = this.cardFor(handle);
      if (!card || !this.modal) return;
      this.modalHandle = handle;
      this.modalTitle.textContent = card.dataset.name;
      this.modalImage.src = card.dataset.imageLarge || card.dataset.image;
      this.modalImage.alt = card.dataset.name;
      const selected = this.selected.includes(handle);
      this.modalSelect.textContent = selected ? 'Remove from Bundle' : 'Add to Bundle';
      this.modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      this.modal.querySelector('[data-close-modal]')?.focus();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.documentElement.style.overflow = '';
      this.modalHandle = null;
    }

    async addBundleToCart(button) {
      if (!this.size || this.selected.length !== this.size || !this.bundleVariant?.id) return;
      const selectedCards = this.selected.map(handle => this.cardFor(handle)).filter(Boolean);
      const groupId = `EP-${Date.now().toString(36).toUpperCase()}`;
      const properties = {
        'Bundle Size': `Pick ${this.size}`,
        'Selected Patches': selectedCards.map(card => card.dataset.name).join(', '),
        '_EverPatch Bundle ID': groupId,
        '_Selected Patch Handles': this.selected.join('|')
      };
      selectedCards.forEach((card, index) => { properties[`Patch ${index + 1}`] = card.dataset.name; });

      this.addButtons.forEach(btn => { btn.disabled = true; btn.textContent = 'Adding bundle…'; });
      this.flash('Adding your bundle to cart…');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: this.bundleVariant.id, quantity: 1, properties }] })
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add bundle to cart.');
        }
        this.flash('Bundle added ✓');
        try { sessionStorage.removeItem(this.storageKey); } catch (_) {}
        setTimeout(() => { window.location.href = `${window.Shopify?.routes?.root || '/'}cart`; }, 550);
      } catch (error) {
        this.flash(error.message || 'Something went wrong. Please try again.');
        this.render();
      }
    }

    persist() {
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ size: this.size, selected: this.selected })); } catch (_) {}
    }

    restore() {
      const params = new URLSearchParams(location.search);
      const urlSize = Number(params.get('bundle'));
      const urlPatches = (params.get('patches') || '').split(',').filter(Boolean);
      let state = null;
      try { state = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch (_) {}
      const candidateSize = [2,3,5,10].includes(urlSize) ? urlSize : Number(state?.size);
      if ([2,3,5,10].includes(candidateSize) && this.bundleVariants.get(candidateSize)?.available) this.setSize(candidateSize, false);
      const candidateHandles = urlPatches.length ? urlPatches : (Array.isArray(state?.selected) ? state.selected : []);
      this.selected = [...new Set(candidateHandles)].filter(handle => this.cardFor(handle)?.dataset.available === 'true').slice(0, this.size || 10);
    }


    flash(message) {
      this.messageNodes.forEach(node => node.textContent = message);
    }
  }

  function rootEvent(root, eventName, selector, callback) {
    root.addEventListener(eventName, event => {
      const target = event.target.closest(selector);
      if (target && root.contains(target)) callback(target, event);
    });
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, char => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#039;', '"':'&quot;' }[char]));
  }

  const init = () => document.querySelectorAll('[data-everpatch-bundle]').forEach(root => {
    if (!root.dataset.initialized) {
      root.dataset.initialized = 'true';
      new EverPatchBundleBuilder(root);
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
